import React, { useContext, useEffect, useState } from 'react';
import Steps from './Steps';
import { useNavigate } from 'react-router-dom';
import ChildForm from './forms/ChildForm';
import ChildList from './forms/ChildList';
import RegistrationContext from './context/RegistrationContext';
import GetFamilyGuid from './http/GetFamilyGuid';
import SaveChild from './http/SaveChild';
import Alert from '../utils/Alert';

const ChildrenStep = () => {
  const MAX_CHILDREN = 5;
  const { registrationData, cancelRegistration, setGuid } =
    useContext(RegistrationContext);
  const [childList, setChildList] = useState([]);
  const [showForm, setShowForm] = useState(true);
  const [childData, setChildData] = useState(null);
  const [childKey, setChildKey] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const updateList = (list) => {
    sessionStorage.setItem('childList', JSON.stringify(list));
    setChildList(list);
    setError('');
    setShowForm(false);
  };

  useEffect(() => {
    const savedChildList = JSON.parse(sessionStorage.getItem('childList'));
    if (savedChildList && savedChildList.length > 0) {
      setChildList(savedChildList);
      setShowForm(false);
    } else {
      setShowForm(true);
    }
  }, []);

  const openForm = (key, data) => {
    if ((key === null || key === undefined) && childList.length >= MAX_CHILDREN) return;
    setChildKey(key);
    setChildData(data);
    setError('');
    setShowForm(true);
  };

  const deleteChild = (key) => {
    const newChildList = [...childList];
    newChildList.splice(key, 1);
    if (newChildList.length === 0) {
      updateList([]);
      setShowForm(true);
    } else {
      updateList(newChildList);
    }
  };

  const handleCancelRegistration = async () => {
    cancelRegistration();
    navigate('/cancel-registration');
  };

  const handleCancelAnotherChildRegistration = async () => {
    navigate('/children-step');
  };

  const handleContinue = async () => {
    if (isSubmitting) return;

    if (childList.length === 0) {
      setError('Please add at least one child to continue.');
      return;
    }

    const email = registrationData?.email || sessionStorage.getItem('email');
    if (!email) {
      setError('Email is missing. Cannot continue.');
      return;
    }

    sessionStorage.setItem('email', email);

    const guid = registrationData?.guid || sessionStorage.getItem('guid');
    if (!guid) {
      setError('GUID is missing. Please restart the registration.');
      return;
    }

    setIsSubmitting(true);

    try {
      const dbGuid = await GetFamilyGuid(email);

      if (!dbGuid || dbGuid !== guid) {
        setError(
          'Looks like you are registered but need to confirm your email address. Please check your email and look for an email from LeapFrog Lab to confirm your registration.\n' +
          '\n' +
          '\n' +
          'Please contact LeapFrogLab@leapfrog.com with any questions. Thanks!'
        );
        setIsSubmitting(false);
        return;
      }

      setGuid(guid);
      sessionStorage.setItem('guid', guid);

      for (const child of childList) {
        await SaveChild(child, email);
      }

      navigate('/registration-agreement');
    } catch (error) {
      console.error(error);
      setError('There was an error validating your registration. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="child-step-page page-container">
      <div className="page-title">
        <h1>LeapFrog Lab Registration</h1>
      </div>
      <Steps activeItem={2} />
      {error && <Alert message={error} />}

      {showForm && (
        <ChildForm
          setChildList={updateList}
          childList={childList}
          childData={childData}
          setChildKey={setChildKey}
          childKey={childKey}
          setShowForm={setShowForm}
          cancelMessage={
            typeof childKey === 'number'
              ? "Are you sure you want to cancel editing this child? Any changes made for this child will be lost."
              : "Are you sure you want to cancel adding this child? Any details for this child will be lost."
          }
          cancelRegistration={() => setShowForm(false)}
        />
      )}

      {!showForm && (
        <ChildList
          childList={childList}
          openForm={openForm}
          deleteChild={deleteChild}
          handleContinue={handleContinue}
          cancelMessage="Are you sure you want to cancel registration? This will delete all family and guardian information entered so far."
          cancelRegistration={handleCancelRegistration}
          maxChildrenReached={childList.length >= MAX_CHILDREN}
          addChildLabel={childList.length === 0 ? '+ Add child' : '+ Add another child'}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default ChildrenStep;
